import { AncientOne, GameState, Investigator, Location, MythosCard } from '../api';

export const investigatorSkills = [
  ['speed', 'Spd'], ['sneak', 'Snk'], ['fight', 'Fgt'], ['will', 'Wil'], ['lore', 'Lor'], ['luck', 'Lck'],
] as const;

export const investigatorConditions = (investigator: Investigator, locations: Location[] = []): string[] => [
  investigator.delayed ? 'Delayed' : '',
  investigator.explored ? 'Explored' : '',
  investigator.isDevoured ? 'Devoured' : '',
  locations.some(location => location.name === 'Jail Cell' && location.investigatorNames?.includes(investigator.name)) ? 'Arrested' : '',
  ...(investigator.specialCards ?? []).filter(card => /bless|curse|deputy|loan/i.test(card.name)).map(card => card.title || card.name),
  investigator.hasBastToken ? 'Bast token' : '',
  investigator.eihortBroodTokens ? `Brood: ${investigator.eihortBroodTokens}` : '',
].filter(Boolean);

export const ancientOneCounters = (ancientOne?: AncientOne): Array<[string, string | number]> => {
  if (!ancientOne) return [];
  const data = ancientOne.specialData;
  if (["Shudde M'ell", 'Abhoth', 'Glaaki', 'Nyogtha'].includes(ancientOne.name)) {
    switch (ancientOne.name) {
      case "Shudde M'ell": return [['Rubble', Array.isArray(data) ? data.length : 0]];
      case 'Abhoth': return [['Children', Array.isArray(data) ? data.length : 0]];
      case 'Glaaki': return [['Servants', Array.isArray(data) ? data.length : 0]];
      case 'Nyogtha': return [['Tendrils', Array.isArray(data) ? data.length : 0]];
    }
  }
  const counters = data && typeof data === 'object' ? data : {};
  switch (ancientOne.name) {
    case 'Rhan-Tegoth': return [['Cultists', counters.cultists?.length ?? 0]];
    case 'Bokrug': return [['Beings of Ib', counters.beingsOfIb?.length ?? 0], ['Clues on Bokrug', counters.clueCount ?? 0]];
    case 'Ghatanothoa': return [['Visages', `${counters.revealedCount ?? 0}/4`]];
    case 'Zhar': return [['Form', counters.firstDefeatOccurred ? 'Second' : 'First']];
    default: return [];
  }
};

/** Board totals; the monster limit counts Arkham and the Sky, excluding Outskirts. */
export const boardCounts = (locations: Location[] = []) => {
  const board = locations.filter(location => location.type !== 'Special' && !/ (1|2)$/.test(location.name));
  return {
    gates: board.reduce((sum, location) => sum + (location.gates?.length ?? 0), 0),
    seals: board.filter(location => location.isSealed).length,
    monsters: locations.filter(location => location.name !== 'Outskirts' && (location.city === 'Arkham' || location.name === 'Sky'))
      .reduce((sum, location) => sum + (location.monsters?.length ?? 0), 0),
    outskirts: locations.find(location => location.name === 'Outskirts')?.monsters?.length ?? 0,
  };
};

export const enabledBoards = (state: GameState) => ({
  dunwich: (state.gameSettings?.includeDunwichCards ?? state.includeDunwichCards) && (state.gameSettings?.enableDunwichBoard ?? true),
  kingsport: !!state.gameSettings?.includeKingsportCards && (state.gameSettings?.enableKingsportBoard ?? true),
  innsmouth: !!state.gameSettings?.includeInsmouthCards && (state.gameSettings?.enableInnsmouthBoard ?? true),
});

export const readableLabel = (value: string) => value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');

export const rumorProgress = (card: MythosCard): string[] => {
  const data = card.rumorData;
  if (typeof data === 'number') return [`Tokens: ${data}`];
  if (Array.isArray(data)) return [`Monsters on rumor: ${data.length}`];
  if (data && typeof data === 'object') return Object.entries(data)
    .filter(([, value]) => typeof value === 'number' || typeof value === 'boolean' || Array.isArray(value))
    .map(([key, value]) => `${readableLabel(key)}: ${Array.isArray(value)
      ? value.every(entry => typeof entry === 'string') && value.length > 0 ? value.join(', ') : value.length
      : value}`);
  return [];
};
